import { styled } from '@mui/material';
import { PartnersCapitalCategory, Schema } from 'further-types/lp/account';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import TabsPanel from 'further-ui/components/TabsPanel';
import {
  account as accountRules,
  transactionType as transactionTypeRules,
} from 'further-ui/validations';
import useAccountsManagement from 'hooks/lp/data/accounts/useAccountsManagement';
import useTransactionTypesManagement from 'hooks/lp/data/accounts/useTransactionTypesManagement';
import { useCallback, useRef, useState } from 'react';
import { z } from 'zod';
import AccountFormContent, {
  type AccountFormContentRef,
} from './AccountFormContent';
import TransactionTypeFormContent, {
  type TransactionTypeData,
  type TransactionTypeFormContentRef,
} from './TransactionTypeFormContent';

type AccountFormValues = z.infer<typeof accountRules.CreateAccount>;
type TransactionTypeFormValues = z.infer<
  typeof transactionTypeRules.CreateTransactionType
>;

enum Tab {
  Account = 'account',
  TransactionType = 'transactionType',
}

export type AccountData = {
  id: string;
  section: Schema.AccountSection;
  accountName: string;
  accountCode: string;
  payableReceivableCategory?: Schema.PayableReceivableCategory | null;
  partnersCapitalCategory?: PartnersCapitalCategory | null;
  balanceSheetSection?: Schema.BalanceSheetSection;
  isCashAccount: boolean;
  transactionsCount: number;
};

const TabsContent = styled(Column)({
  marginTop: -20,
});

const FormsContainer = styled('div')({
  display: 'grid',
  '& > *': {
    gridRow: 1,
    gridColumn: 1,
  },
});

const FormSlot = styled('div')<{ active: boolean }>(({ active }) => ({
  visibility: active ? 'visible' : 'hidden',
  pointerEvents: active ? 'auto' : 'none',
}));

type Props = {
  onClose: () => void;
  vehicleId: string;
  prepaidExpenseAccountId?: string;
  deferredIncomeAccountId?: string;
  bankTransferReceivableAccountId?: string;
  account?: AccountData;
  selectedAccountId?: string;
  transactionType?: TransactionTypeData;
  defaultToTransactionTypeTab?: boolean;
};

const AddNewDialog: React.FC<Props> = ({
  onClose,
  vehicleId,
  prepaidExpenseAccountId,
  deferredIncomeAccountId,
  account,
  selectedAccountId,
  transactionType,
  defaultToTransactionTypeTab,
}) => {
  const defaultTab =
    (selectedAccountId && !account) || defaultToTransactionTypeTab
      ? Tab.TransactionType
      : Tab.Account;
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);
  const accountFormRef = useRef<AccountFormContentRef>(null);
  const transactionTypeFormRef = useRef<TransactionTypeFormContentRef>(null);

  const { createAccount, updateAccount } = useAccountsManagement({
    onSuccess: onClose,
  });
  const { createTransactionType, updateTransactionType } =
    useTransactionTypesManagement({ onSuccess: onClose });

  const handleAccountSubmit = useCallback(
    (data: AccountFormValues) => {
      if (account) {
        updateAccount.mutate({
          ...data,
          id: account.id,
          vehicleId,
        });
      } else {
        createAccount.mutate({ ...data, vehicleId });
      }
    },
    [account, createAccount, updateAccount, vehicleId],
  );

  const handleTransactionTypeSubmit = useCallback(
    (data: TransactionTypeFormValues) => {
      if (transactionType) {
        updateTransactionType.mutate({
          vehicleId,
          transactionTypeId: transactionType.id,
          name: data.name,
        });
      } else {
        createTransactionType.mutate({ ...data, vehicleId });
      }
    },
    [transactionType, createTransactionType, updateTransactionType, vehicleId],
  );

  const handleSave = () => {
    if (transactionType || activeTab === Tab.TransactionType) {
      transactionTypeFormRef.current?.submit();
    } else {
      accountFormRef.current?.submit();
    }
  };

  const title = account
    ? 'Edit account'
    : transactionType
    ? 'Edit transaction type'
    : selectedAccountId
    ? 'Add transaction type'
    : 'Add item';

  const tabHeaders = [
    { key: Tab.Account, label: 'Account', content: null },
    { key: Tab.TransactionType, label: 'Transaction type', content: null },
  ];

  return (
    <ConfirmDialog
      title={title}
      open
      onClose={onClose}
      maxWidth="sm"
      withoutDivider
      loading={
        createAccount.isPending ||
        updateAccount.isPending ||
        createTransactionType.isPending ||
        updateTransactionType.isPending
      }
      actions={{
        confirm: {
          onClick: handleSave,
          label: 'Save',
        },
        cancel: {
          onClick: onClose,
        },
      }}
      content={
        account ? (
          <AccountFormContent
            ref={accountFormRef}
            onSubmit={handleAccountSubmit}
            account={account}
            isCashAccount={account.isCashAccount}
            vehicleId={vehicleId}
            prepaidExpenseAccountId={prepaidExpenseAccountId}
            deferredIncomeAccountId={deferredIncomeAccountId}
          />
        ) : transactionType ? (
          <TransactionTypeFormContent
            ref={transactionTypeFormRef}
            transactionType={transactionType}
            onSubmit={handleTransactionTypeSubmit}
          />
        ) : selectedAccountId ? (
          <TransactionTypeFormContent
            ref={transactionTypeFormRef}
            selectedAccountId={selectedAccountId}
            onSubmit={handleTransactionTypeSubmit}
          />
        ) : (
          <TabsContent spacing="lg">
            <TabsPanel
              defaultTab={defaultTab}
              contentSpacing="none"
              onTabChange={(tab) => setActiveTab(tab)}
              tabs={tabHeaders}
            />
            <FormsContainer>
              <FormSlot active={activeTab === Tab.Account}>
                <AccountFormContent
                  ref={accountFormRef}
                  onSubmit={handleAccountSubmit}
                  vehicleId={vehicleId}
                  prepaidExpenseAccountId={prepaidExpenseAccountId}
                  deferredIncomeAccountId={deferredIncomeAccountId}
                />
              </FormSlot>
              <FormSlot active={activeTab === Tab.TransactionType}>
                <TransactionTypeFormContent
                  ref={transactionTypeFormRef}
                  selectedAccountId={selectedAccountId}
                  onSubmit={handleTransactionTypeSubmit}
                />
              </FormSlot>
            </FormsContainer>
          </TabsContent>
        )
      }
    />
  );
};

export default AddNewDialog;
